import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";

import CommonButton from "../utilities/formElements/commonButton/CommonButton";
import CommonSelect from "../utilities/formElements/commonSelect/CommonSelect";
import CommonTestArea from "../utilities/formElements/commonTextArea/CommonTestArea";
import { cancelSubscriptionValidationSchema } from "../utilities/validationSchema";
import {
  toastMessageError,
  toastMessageSuccess,
} from "../utilities/commonToastMessage";

import { subscription } from "../../services/susbscription";
import { reasonForClosingAccount } from "../../constants/commonConstants";

import { CommonModalProps } from "../../Interfaces/CommonModalInterface";

interface ICancelSubscriptionForm {
  selectedReason: string;
  comment?: string;
}

// Cancellation is honest, not hidden - kept right next to "Change" in
// Settings.tsx - but this confirmation step is the deliberate speed bump
// against misclicks, matching the deferred-downgrade design already agreed
// on: full paid access continues through the already-paid period, no
// refund for the remainder, only THEN does it actually downgrade. The
// reason/comment fields mirror the exact same feedback-collection pattern
// the "Delete Account" flow already uses (CommonModel.tsx's "We really
// don't want to see you go..." form) - same value for churn/downgrade
// reasons, just captured here instead.
const CancelSubscriptionModal: React.FC<CommonModalProps> = ({
  title,
  onClose,
}) => {
  const { t: translation } = useTranslation();
  const authReducer: any = useSelector((state: any) => state?.AuthReducer);
  const [loading, setLoading] = useState(false);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(cancelSubscriptionValidationSchema(translation)),
  });

  const currentPeriodEnd = authReducer?.accountRecord?.currentPeriodEnd;
  const planTitle = authReducer?.accountRecord?.subscriptionData?.title;

  const onSubmit = async (data: ICancelSubscriptionForm) => {
    setLoading(true);
    const result = await subscription({
      jsonprc: "2.0",
      method: "cancelSubscription",
      params: {
        userId: authReducer.authData._id,
        selectedReason: data.selectedReason,
        comment: data.comment,
      },
      id: 0,
    });
    setLoading(false);
    if (result && result.data) {
      toastMessageSuccess(
        translation("subscription_cancelled_scheduled"),
        "subscriptionCancelled"
      );
      onClose();
    } else {
      toastMessageError(translation("something_went_wrong"), "cancelFailed");
    }
  };

  return (
    <div className="modal show-modal common-modal">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">{title}</h4>
            <CommonButton
              type="button"
              className="btnC btn-close btn-close-white"
              onClick={onClose}
              data-bs-dismiss="modal"
            />
          </div>

          <div className="modal-body common-body text-primary">
            <p>
              {translation("cancel_subscription_explainer_1")} {planTitle}{" "}
              {translation("cancel_subscription_explainer_2")}{" "}
              {currentPeriodEnd
                ? new Date(currentPeriodEnd).toLocaleDateString()
                : ""}
              . {translation("cancel_subscription_explainer_3")}
            </p>

            <form onSubmit={handleSubmit((data) => onSubmit(data as ICancelSubscriptionForm))}>
              <p className="error-para-show">
                {translation("we_really_dont_want_to_see_you_go")}
              </p>

              <div className="form-group">
                <label>{translation("why_cancelling_your_subscription")}</label>
                <CommonSelect
                  option={reasonForClosingAccount}
                  name="selectedReason"
                  className="form-select form-control"
                  control={control}
                  errors={errors?.selectedReason}
                />
              </div>

              <div className="form-group">
                <label>{translation("your_feedback_matters")}</label>
                <label>
                  {translation("anything_else_you_like_us_to_know")}
                </label>
                <CommonTestArea
                  control={control}
                  name="comment"
                  className="form-control"
                  error={errors?.comment}
                />
              </div>

              <div className="text-center mt-4 mb-3">
                <CommonButton
                  type="button"
                  className="theme-btn white-btn text-uppercase fontBold me-3"
                  label={translation("keep_my_plan")}
                  onClick={onClose}
                />
                <CommonButton
                  type="submit"
                  className="theme-btn success-btn text-uppercase fontBold"
                  label={translation("confirm_cancellation")}
                  loading={loading}
                  disabled={loading}
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancelSubscriptionModal;
